import {
  type FeedbackRef,
  frownIcon,
  frustratedIcon,
  grinIcon,
  neutralIcon,
  smileIcon,
  UserFeedback,
} from '@github-ui/user-feedback'
import {type Ref, type RefObject, useCallback, useImperativeHandle, useRef} from 'react'

import {apiPostFeedback, type PostMemexFeedbackBody} from '../api/memex/api-post-feedback'
import {FeedbackDialogResources} from '../strings'

const CONTENT_CHARACTER_LIMIT = 2000

const ratingOptions = [
  {
    name: 'Hate',
    value: 1 as const,
    icon: frustratedIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Dislike',
    value: 2 as const,
    icon: frownIcon,
    color: 'veryDissatisfied' as const,
  },
  {
    name: 'Neutral',
    value: 3 as const,
    icon: neutralIcon,
    color: 'dissatisfied' as const,
  },
  {
    name: 'Like',
    value: 4 as const,
    icon: smileIcon,
    color: 'verySatisfied' as const,
  },
  {
    name: 'Love',
    value: 5 as const,
    icon: grinIcon,
    color: 'verySatisfied' as const,
  },
]

type Rating = (typeof ratingOptions)[number]['value']
export type FeedbackDialogRef = FeedbackRef<Rating>

type FeedbackDialogProps = {
  returnFocusRef?: RefObject<HTMLElement | null>
  ref?: Ref<FeedbackDialogRef>
}

export function FeedbackDialog({ref, returnFocusRef}: FeedbackDialogProps) {
  const feedbackDialogRef = useRef<FeedbackDialogRef>(null)
  const initialRatingRef = useRef<Rating | undefined>(undefined)

  useImperativeHandle(ref, () => ({
    openDialog: (rating?: Rating) => {
      initialRatingRef.current = rating

      feedbackDialogRef.current?.openDialog(rating)
    },
  }))

  const onSubmit = useCallback(async (rating: Rating | null, text: string): Promise<Array<string>> => {
    const errors = []

    if (rating == null) {
      errors.push(FeedbackDialogResources.noRatingError)
    }

    if (text.length > CONTENT_CHARACTER_LIMIT) {
      errors.push(FeedbackDialogResources.limitExceededError(CONTENT_CHARACTER_LIMIT))
    }

    if (errors.length > 0) {
      return errors
    }

    const body: PostMemexFeedbackBody = {
      payload: {
        rating: rating || 0,
        content: text,
      },
    }

    try {
      const response = await apiPostFeedback(body)
      if (!response.success) {
        errors.push(FeedbackDialogResources.submissionError)
      }
    } catch {
      errors.push(FeedbackDialogResources.submissionError)
    }

    return errors
  }, [])

  return (
    <>
      <UserFeedback
        ref={feedbackDialogRef}
        options={ratingOptions}
        onSubmit={onSubmit}
        returnFocusRef={returnFocusRef}
      />
    </>
  )
}

try{ FeedbackDialog.displayName ||= 'FeedbackDialog' } catch {}